import clsx from "clsx";
import { debounce } from "lodash-es";
import { FC, lazy, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  MIN_WHITEBOARD_WIDTH_PX,
  WHITEBOARD_TOOLBAR_WIDTH_PX,
} from "./Whiteboard/TldrawWhiteboard/helpers";

import { SCROLL_DEBOUNCER_MS } from "components/materials/presentation/projector/ProjectorScroller";
import { useCapabilities } from "store/capability-hooks";

const WhiteboardField = lazy(() => import("./Whiteboard/WhiteboardField"));

interface ImageAssetProps {
  name: string;
  url: string;
  annotations?: boolean;
  blockId?: string;
  fieldId?: string;
  isInModal?: boolean;
  isFeaturingAsset?: boolean;
  blockHeight?: number;
}

export const ImageAsset: FC<ImageAssetProps> = ({
  name,
  url,
  annotations,
  blockId,
  fieldId,
  isInModal,
  isFeaturingAsset,
  blockHeight,
}) => {
  const { canAuthorSection } = useCapabilities();

  const [whiteboardHeight, setWhiteboardHeight] = useState(0);
  const [whiteboardWidth, setWhiteboardWidth] = useState(0);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [offsetLeftForSmallPage, setOffsetLeftForSmallPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [imageHeightExceedsBlockHeight, setImageHeightExceedsBlockHeight] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const imageContainerRef = useRef<HTMLDivElement | null>(null);

  const updateWhiteboardSettings = (imageElement: HTMLImageElement) => {
    const { naturalWidth, width, height } = imageElement;
    setWhiteboardHeight(height);
    setWhiteboardWidth(Math.max(width, MIN_WHITEBOARD_WIDTH_PX));

    if (width < MIN_WHITEBOARD_WIDTH_PX) {
      // If we have to increase the width of the whiteboard in order to fit the toolbar,
      // we need to account for the extra bit of offset that gets added to the left,
      // and pass that to the whiteboard component.
      setOffsetLeftForSmallPage((MIN_WHITEBOARD_WIDTH_PX - width) / 2);
    } else {
      setOffsetLeftForSmallPage(0);
    }

    setZoom(width / naturalWidth);
  };

  const debouncedUpdateWhiteboardSettings = useMemo(
    () => debounce(updateWhiteboardSettings, SCROLL_DEBOUNCER_MS),
    [],
  );

  const updateOffsetLeft = () => {
    if (!imageRef.current) {
      return;
    }
    setOffsetLeft(imageRef.current.offsetLeft);
  };

  const debouncedUpdateOffsetLeft = useMemo(
    () => debounce(updateOffsetLeft, SCROLL_DEBOUNCER_MS),
    [],
  );

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    updateWhiteboardSettings(img);
    updateOffsetLeft();

    if (blockHeight && img.height > blockHeight) {
      setImageHeightExceedsBlockHeight(true);
    }
  };

  useLayoutEffect(() => {
    const imageObserver = new ResizeObserver((entries) => {
      if (entries[0].contentRect.width === 0 || entries[0].contentRect.height === 0) {
        return;
      }
      debouncedUpdateWhiteboardSettings(entries[0].target as HTMLImageElement);
    });

    imageObserver.observe(imageRef.current);

    const imageContainerObserver = new ResizeObserver((entries) => {
      if (entries[0].contentRect.width === 0 || entries[0].contentRect.height === 0) {
        return;
      }
      debouncedUpdateOffsetLeft();
    });

    imageContainerObserver.observe(imageContainerRef.current);

    return () => {
      imageObserver.disconnect();
      imageContainerObserver.disconnect();
    };
  }, [debouncedUpdateWhiteboardSettings, debouncedUpdateOffsetLeft]);

  return (
    <div className="relative flex w-full items-start justify-center">
      <div className="flex justify-center" ref={imageContainerRef}>
        <img
          alt={name}
          className="h-auto w-auto max-w-full object-contain"
          data-vertical-asset={imageHeightExceedsBlockHeight}
          ref={imageRef}
          src={url}
          onLoad={handleLoad}
        />
      </div>
      {annotations && blockId && fieldId && (
        <div
          className={clsx("absolute top-0", {
            "left-0": offsetLeft === 0,
            "large-whiteboard": whiteboardWidth > WHITEBOARD_TOOLBAR_WIDTH_PX * 2,
          })}
          style={{
            height: `${whiteboardHeight}px`,
            width: `${whiteboardWidth}px`,
          }}
        >
          <WhiteboardField
            blockId={blockId}
            fieldId={fieldId}
            forceReadOnly={isFeaturingAsset && !canAuthorSection}
            isInModal={isInModal}
            offsetLeft={offsetLeftForSmallPage}
            zoom={zoom}
            isOverlay
          />
        </div>
      )}
    </div>
  );
};
